import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { Page, ButtonGroup, Button, Card, Text, BlockStack, Divider, Select, TextField, InlineStack, Box, DataTable, Thumbnail, Badge, Icon, PageActions, DescriptionList } from '@shopify/polaris';
import {
  BarcodeIcon,
  ProductIcon,
  AlertCircleIcon
} from '@shopify/polaris-icons';

import PageFooter from '../partials/PageFooter';
import LoadingSpinner from '../partials/LoadingSpinner';
import RequiresRefreshBanner from '../partials/RequiresRefreshBanner';

import { addInventory, subtractInventory, setInventory, scanInventory, addPlaceholderAction, clearActions, dismissInventoryScan, setEventsFromSession, saveSessionCsv } from '../../redux/actions/inventorySessions';
import { locationsIndex } from '../../redux/actions/locations';

const LiveModify = ({ shopUrl, 
                      payload, 
                      inventorySession,
                      locations,
                      postAddInventory,
                      postSubtractInventory,
                      postSetInventory,
                      postScanInventory,
                      // postTransferInventory,
                      dismissScan,
                      addAction,
                      setEvents,
                      clearSession,
                      saveSession,
                      loadLocations,
                      requiresRefresh }) => {
  const [actionType, setActionType] = useState('scan');

  const [locationsList, setLocationsList] = useState([]);

  const [actionQuantity, setActionQuantity] = useState('');
  const [location, setLocation] = useState('');
  const [scanTerm, setScanTerm] = useState('');
  const [scanFocused, setScanFocused] = useState('');

  useEffect(() => {
    loadLocations(shopUrl, payload);
    loadSession();
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(scanTerm !== ''){
        addAction(scanTerm, actionType);
        submitAction();
        setScanTerm('');
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [scanTerm, 500]);

  useEffect(() => {
    if(locations.status === 'success') {

      let tempLocations = [];
      locations.locations.forEach((location) => {
        tempLocations.push({ label: location.name, value: location.id })
      })

      setLocationsList(tempLocations);
    }
  }, [locations]);

  const handleLocationSelect = (event) => {
    let matchLocation = locations.locations.find(matchLocation => matchLocation.id === event);
    setLocation(matchLocation);
  }

  const updateScan = (event) => {
    setScanTerm(event);
  }

  const validateQuantity = (event) => {    
    setActionQuantity(event.replace(/\D/g,''));
  }

  const loadSession = () => {
    let loadedSession = JSON.parse(localStorage.getItem("liveInventorySession"));

    if(loadedSession) {
      setEvents(loadedSession);
    }
  }

  const submitAction = () => {
    if((!actionQuantity && actionType !== 'scan') || (!location && actionType !== 'transfer')) { dismissScan(inventorySession.events.length) }

    if (actionQuantity && location && actionType === 'add') { postAddInventory(shopUrl, payload, scanTerm, inventorySession.events.length, actionQuantity, location) }
    if (actionQuantity && location && actionType === 'subtract') { postSubtractInventory(shopUrl, payload, scanTerm, inventorySession.events.length, actionQuantity, location) }
    if (actionQuantity && location && actionType === 'set') { postSetInventory(shopUrl, payload, scanTerm, inventorySession.events.length, actionQuantity, location) }
    if (location && actionType === 'scan') { postScanInventory(shopUrl, payload, scanTerm, inventorySession.events.length, location) }
    // if (actionQuantity && fromLocation && toLocation && actionType === 'transfer') { postTransferInventory(shopUrl, payload, scanTerm, inventorySession.events.length, actionQuantity, fromLocation, toLocation) }
  }

  const actionChange = (e) => {
    setActionType(e);
  }

  const downloadActions = () => {
    let rows = [['Location', 'Name', 'Options', 'SKU', 'Barcode', 'Price', 'Action Type', 'Action Quantity', 'Quantity', 'Updated Quantity']];

    inventorySession.events.toReversed().map(event =>
      appendAction(rows, event)
    )

    let csvContent = "data:text/csv;charset=utf-8," 
        + rows.map(e => e.join(",")).join("\n");

    var encodedUri = encodeURI(csvContent);
    window.open(encodedUri);
  }

  const appendAction = (rowsArray, event) => {
    let options = [];
    let row = [];

    if(event.actionType === 'set' || event.actionType === 'add' || event.actionType === 'subtract') {
      options = [];

      event?.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.selectedOptions && event.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.selectedOptions.map(option =>
        options.push(`${option.name} -- ${option.value}`)
      )

      row = [
        event.locationName,
        event.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.product?.title,
        options.join(' | '),
        event.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.sku,
        event.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.barcode,
        event.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.price,
        event.actionType,
        event.actionQuantity,
        (event.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.inventoryQuantity - event.inventoryAdjustmentGroup?.changes[0]?.delta),
        event.inventoryAdjustmentGroup?.changes[0]?.item?.variant?.inventoryQuantity
      ]
    }

    if(event.actionType === 'scan') {
      options = [];

      event['node']?.selectedOptions && event['node']?.selectedOptions.map(option =>
        options.push(`${option.name} -- ${option.value}`)
      )
      
      row = [
        event.locationName,
        event['node']?.displayName,
        options.join(' | '),
        event['node']?.sku,
        event['node']?.barcode,
        event['node']?.price,
        event.actionType,
        event.actionQuantity,
        event['node']?.inventoryItem?.inventoryLevel?.quantities[0]?.quantity,
        event['node']?.inventoryItem?.inventoryLevel?.quantities[0]?.quantity
      ]
    }

    rowsArray.push(row);
  }

  const liveRowImage = (event) => {
    if (event['node']?.product?.media?.edges[0]?.node?.preview?.image) {
      return <Thumbnail
          source={event['node'].product.media.edges[0].node.preview.image.url}
          size="small"
          alt={event['node'].product.media.edges[0].node.preview.image.alt}/>
    }

    return <Icon source={ProductIcon} tone="base" />
  }

  const saveAndClearSession = () => {
    let rows = [['Location', 'Name', 'Options', 'SKU', 'Barcode', 'Price', 'Action Type', 'Action Quantity', 'Quantity', 'Updated Quantity']];

    inventorySession.events.toReversed().map(event =>
      appendAction(rows, event)
    )

    let csvContent = rows.map(e => e.join(",")).join("\n");

    saveSession(shopUrl, payload, csvContent);
    clearSession();
  }

  const liveRowInventoryAdjustmentImage = (event) => {
    if (event.inventoryAdjustmentGroup.changes[0].item.variant.product.media.edges[0]?.node?.preview?.image) { 
      return <Thumbnail
          source={event.inventoryAdjustmentGroup.changes[0].item.variant.product.media.edges[0].node.preview.image.url}
          size="small"
          alt={event.inventoryAdjustmentGroup.changes[0].item.variant.product.media.edges[0].node.preview.image.alt}/> 
    }
    
    return <Icon source={ProductIcon} tone="base" />
  }

  const optionBadge = (option) => {
    return <div key={option.name}>
      <Badge 
        tone="info"
        key={option.name}
      >
        <Text>{option.value}</Text>
      </Badge>
       &nbsp;
    </div>
  }

  const eventRow = (event, index) => {
    if(event.error) {
      return [
        '',
        <Icon source={AlertCircleIcon} tone="critical" />,
        `${ event.error }`,
        '',
        '',
        '',
        '',
        ''
      ]
    }

    if(event.placeholder) {
      let spinner = ''

      if(inventorySession.fetchingList.indexOf(event.actionId) >= 0) {
        spinner = <LoadingSpinner size="small" />
      }

      return [
        '',
        spinner,
        `${event.actionType}: ${event.sku}`,
        '',
        '',
        '',
        '',
        ''
      ]
    }

    if(event.actionType === 'scan') {
      let options = event['node'].selectedOptions;

      return [
        <Text as="span" tone="subdued">View</Text>,
        liveRowImage(event),
        <Text as="strong" fontWeight="semibold">{event['node'].displayName}</Text>,
        event['node'].sku,
        event['node'].barcode,
        (options.length > 0 ? options.map((option) => optionBadge(option)) : ''),
        event['node'].inventoryItem?.inventoryLevel?.quantities[0]?.quantity || 'N/A',
        <Text as="strong" fontWeight="bold">{event['node'].inventoryItem?.inventoryLevel?.quantities[0]?.quantity || 'N/A'}</Text>
      ]
    }

    if(event.actionType === 'transfer') {}

    if(event.actionType === 'set') {
      let options = event.inventoryAdjustmentGroup.changes[0].item.variant.selectedOptions;

      return [
        <Text as="span" tone="subdued">Set</Text>,
        liveRowInventoryAdjustmentImage(event),
        <Text as="strong" fontWeight="semibold">{event.inventoryAdjustmentGroup.changes[0].item.variant.product.title}</Text>,
        event.inventoryAdjustmentGroup.changes[0].item.variant.sku,
        event.inventoryAdjustmentGroup.changes[0].item.variant.barcode,
        (options.length > 0 ? options.map((option) => optionBadge(option)) : ''),
        event.inventoryAdjustmentGroup.changes[0].item.variant.inventoryItem.inventoryLevel?.quantities[0]?.quantity - event.inventoryAdjustmentGroup.changes[0].delta,
        <Text as="strong" fontWeight="bold">{event.inventoryAdjustmentGroup.changes[0].item.variant.inventoryItem.inventoryLevel?.quantities[0]?.quantity}</Text>
      ]
    }

    if(event.actionType === 'add') {
      let options = event.inventoryAdjustmentGroup.changes[0].item.variant.selectedOptions;

      return [
        <Text as="span" tone="subdued">Add</Text>,
        liveRowInventoryAdjustmentImage(event),
        <Text as="strong" fontWeight="semibold">{event.inventoryAdjustmentGroup.changes[0].item.variant.product.title}</Text>,
        event.inventoryAdjustmentGroup.changes[0].item.variant.sku,
        event.inventoryAdjustmentGroup.changes[0].item.variant.barcode,
        (options.length > 0 ? options.map((option) => optionBadge(option)) : ''),
        event.inventoryAdjustmentGroup.changes[0].item.variant.inventoryItem.inventoryLevel?.quantities[0]?.quantity - event.inventoryAdjustmentGroup.changes[0].delta,
        <Text as="strong" fontWeight="bold">{event.inventoryAdjustmentGroup.changes[0].item.variant.inventoryItem.inventoryLevel?.quantities[0]?.quantity}</Text>
      ]
    }

    if(event.actionType === 'subtract') {
      let options = event.inventoryAdjustmentGroup.changes[0].item.variant.selectedOptions;

      return [
        <Text as="span" tone="subdued">Subtract</Text>,
        liveRowInventoryAdjustmentImage(event),
        <Text as="strong" fontWeight="semibold">{event.inventoryAdjustmentGroup.changes[0].item.variant.product.title}</Text>,
        event.inventoryAdjustmentGroup.changes[0].item.variant.sku,
        event.inventoryAdjustmentGroup.changes[0].item.variant.barcode,
        (options.length > 0 ? options.map((option) => optionBadge(option)) : ''),
        event.inventoryAdjustmentGroup.changes[0].item.variant.inventoryItem.inventoryLevel?.quantities[0]?.quantity - event.inventoryAdjustmentGroup.changes[0].delta,
        <Text as="strong" fontWeight="bold">{event.inventoryAdjustmentGroup.changes[0].item.variant.inventoryItem.inventoryLevel?.quantities[0]?.quantity}</Text>
      ]
    }
  }

  return(
    <Page
      title="Live Inventory"
      backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
      primaryAction={
          <Button variant="primary" onClick={() => saveAndClearSession()}>
            Save and Clear Session
          </Button>
        }
        secondaryActions={
          <Button variant="secondary" onClick={() => downloadActions()}>
            Download Session Information
          </Button>
        }
    >
      { requiresRefresh &&
        <RequiresRefreshBanner />
      }
      <Card>
        <BlockStack gap="500">
          <DescriptionList
            items={[
              {
                term: 'Action Type',
                description:
                  <ButtonGroup variant="segmented">
                    <Button pressed={actionType==='add'} onClick={() => actionChange('add')}>
                      Add Inventory
                    </Button>
                    <Button pressed={actionType==='subtract'} onClick={() => actionChange('subtract')}>
                      Subtract Inventory
                    </Button>
                    <Button pressed={actionType==='set'} onClick={() => actionChange('set')}>
                      Set Inventory
                    </Button>
                    <Button pressed={actionType==='scan'} onClick={() => actionChange('scan')}>
                      View Inventory
                    </Button>
                  </ButtonGroup>
              },
              {
                term: 'Locations',
                description:
                  <>
                    { locations.status !== 'success' &&
                      <LoadingSpinner size="small" statusText=" Loading locations" />
                    }

                    { locations.status === 'success' &&
                      <Select
                        options={locationsList}
                        onChange={handleLocationSelect}
                        value={location.id}
                        placeholder="Select location"
                      />
                    }
                  </>
              },
              {
                term: 'Quantity',
                description:
                  <TextField
                    label={ (actionType === 'add' || actionType === 'subtract' ) ? 'Change by amount' : 'Set Quantity' }
                    value={actionQuantity}
                    onChange={validateQuantity}
                    autoComplete="off"
                    placeholder={ (actionType === 'add' || actionType === 'subtract' ) ? 'Change by amount' : 'Set Quantity' }
                    disabled={ actionType==='scan' }
                    type="number"
                  />
              }
            ]}
          />

          <Divider />

          <DescriptionList
            items={[
              {
                term: 'Scan Barcode (SKU, UPC, EAN)',
                description:
                  <TextField
                    value={scanTerm}
                    onChange={updateScan}
                    autoComplete="off"
                    placeholder={ scanFocused ? "Scan barcode" : "Click here to begin scanning" }
                    prefix={<Badge tone={ scanFocused ? 'success' : 'critical' }>{ scanFocused ? 'Ready' : 'Not ready' }</Badge>}
                    disabled={ !location || ( (actionType === 'add' || actionType === 'subtract' || actionType === 'set' ) && !actionQuantity ) }
                    onFocus={() => setScanFocused(true)}
                    onBlur={() => setScanFocused(false)}
                    connectedLeft={
                    <Icon
                      source={BarcodeIcon}
                      tone="base"
                    />
                  }
                  />
              }
            ]}
          />
        </BlockStack>
      </Card>

      <Box paddingBlockStart="200">
        <Card>
          <BlockStack gap="200">
            <DataTable
              headings={[
                'Type',
                'Image',
                'Name',
                'SKU',
                'Barcode',
                'Options',
                'Before',
                'Quantity'
              ]}
              columnContentTypes={[
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'numeric',
                'numeric'
              ]}
              hasZebraStripingOnData
              rows={inventorySession.events.toReversed().map((event, index) => eventRow(event, index))}
            />
          </BlockStack>
        </Card>
      </Box>

      <PageActions
        primaryAction={
          <Button variant="primary" onClick={() => saveAndClearSession()}>
            Save and Clear Session
          </Button>
        }
        secondaryActions={
          <Button variant="secondary" onClick={() => downloadActions()}>
            Download Session Information
          </Button>
        }
      />

      <PageFooter stripeBackground='#c66628' helpLink="inventory-management/live-inventory-modification-3/" helpDescription="live inventory updates" />
    </Page>
  )
}

const mapStateToProps = state => ({
  requiresRefresh: state.activeToken.requiresRefresh,
  inventorySession: state.inventorySessions,
  locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  postAddInventory: (shopUrl, payload, barcode, actionId, actionQuantity, location) => dispatch(addInventory(shopUrl, payload, barcode, actionId, actionQuantity, location)),
  postSubtractInventory: (shopUrl, payload, barcode, actionId, actionQuantity, location) => dispatch(subtractInventory(shopUrl, payload, barcode, actionId, actionQuantity, location)),
  postSetInventory: (shopUrl, payload, barcode, actionId, actionQuantity, location) => dispatch(setInventory(shopUrl, payload, barcode, actionId, actionQuantity, location)),
  postScanInventory: (shopUrl, payload, barcode, actionId, location) => dispatch(scanInventory(shopUrl, payload, barcode, actionId, location)),
  // postTransferInventory: (shopUrl, payload, barcode, actionId, actionQuantity, fromLocation, toLocation) => dispatch(transferInventory(shopUrl, payload, barcode, actionId, actionQuantity, fromLocation, toLocation)),
  dismissScan: (actionId) => dispatch(dismissInventoryScan(actionId)),
  addAction: (barcode, actionType) => dispatch(addPlaceholderAction(barcode, actionType)),
  setEvents: (loadedSession) => dispatch(setEventsFromSession(loadedSession)),
  clearSession: () => dispatch(clearActions()),
  loadLocations: (shopUrl, payload) => dispatch(locationsIndex(shopUrl, payload)),
  saveSession: (shopUrl, payload, csvData) => dispatch(saveSessionCsv(shopUrl, payload, csvData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LiveModify);